import { Injectable } from '@angular/core';
import { MessageService } from 'primeng/api';
import { ProductDbService } from '../../../services/productdb.service';
import {
  HoldReceiptInfo,
  Product,
  Quotation,
  QuotationsHttpResponse,
  Transaction,
  TransactionHttpResponse,
  TransactionsHttpResponse,
} from '../../../modals/product.modals';

/**
 * Shared dependency bag for the transaction/quotation-history cluster.
 * Several methods here have async gaps (HTTP subscribe, setTimeout for DOM
 * focus/scroll), so state is exposed as live getters/setters rather than
 * snapshots, consistent with the offer-group-mode and order-mode clusters.
 *
 * Methods NOT in this module's list (disableOfferGroupMode,
 * updateFormDataWithQuotationMode, mapItemsToProducts, DiscountchangeCalc,
 * focusQuantityInput) are exposed as plain callbacks into the component's
 * existing methods.
 */
export interface TransactionHistoryDeps {
  getSavedReceipts: () => HoldReceiptInfo[];
  setSavedReceipts: (value: HoldReceiptInfo[]) => void;

  getTransactions: () => Transaction[];
  setTransactions: (value: Transaction[]) => void;

  getQuotations: () => Quotation[];
  setQuotations: (value: Quotation[]) => void;

  getFilteredQuotations: () => Quotation[];
  setFilteredQuotations: (value: Quotation[]) => void;

  getSelectedQuotationIndex: () => number;
  setSelectedQuotationIndex: (value: number) => void;

  getSelectedQuotationForView: () => string;
  setSelectedQuotationForView: (value: string) => void;

  getQuotationSearchTerm: () => string;
  setQuotationSearchTerm: (value: string) => void;

  getFlattenedTransactions: () => any[];
  setFlattenedTransactions: (value: any[]) => void;

  getSelectedTransactionIndex: () => number;
  setSelectedTransactionIndex: (value: number) => void;

  setSelectedTransactionForPrint: (value: string) => void;
  setCurrentOrderId: (value: string) => void;

  getCurrentSearchTerm: () => string;

  getActiveHistoryTab: () => number;
  setActiveHistoryTab: (value: number) => void;

  getIsExchangeOrder: () => boolean;
  setIsExchangeOrder: (value: boolean) => void;

  getIsWholesaleMode: () => boolean;
  setIsWholesaleMode: (value: boolean) => void;

  getIsOfferGroupMode: () => boolean;

  getIsSuspenseMode: () => boolean;
  setIsSuspenseMode: (value: boolean) => void;

  getIsQuotationMode: () => boolean;
  setIsQuotationMode: (value: boolean) => void;

  getCartProducts: () => Product[];
  setCartProducts: (value: Product[]) => void;
  setProductsTemp: (value: Partial<Product[]>) => void;

  setSelectedInvoiceNo: (value: string) => void;
  setRefundCashAmount: (value: number) => void;
  setHoldOrderId: (value: string) => void;
  setCustomerName: (value: string) => void;
  setDiscountMode: (value: 'percentage' | 'amount') => void;
  getIsSalesDiscountEditEnabled: () => boolean;
  setShowTransactionDialog: (value: boolean) => void;
  getEnableCursorFocus: () => boolean;

  getIsNavigatingProgrammatically: () => boolean;
  setIsNavigatingProgrammatically: (value: boolean) => void;

  disableOfferGroupMode: () => void;
  updateFormDataWithQuotationMode: () => void;
  mapItemsToProducts: (
    items: any[],
    isExchangeOrder: boolean,
    isQuotation: boolean,
  ) => Product[];
  discountChangeCalc: () => void;
  /** Prices lines saved in a secondary sale unit from that unit's mapping */
  restoreSavedLinePrices: (applySavedDiscount: boolean) => void;
  focusQuantityInput: () => void;
}

/**
 * TransactionHistoryService
 *
 * Dedicated service for the transaction/quotation history dialog: loading
 * transaction and quotation lists, search/filtering, row selection and
 * keyboard navigation, and loading a quotation's items into the cart for
 * viewing.
 *
 * Extracted from DashboardComponent to decouple history logic while
 * preserving 100% identical behavior.
 */
@Injectable({
  providedIn: 'root',
})
export class TransactionHistoryService {
  constructor(
    private readonly messageService: MessageService,
    private readonly productService: ProductDbService,
  ) {}

  getAllTransaction(showSavedTransaction: boolean, deps: TransactionHistoryDeps) {
    if (showSavedTransaction == true) {
      this.productService
        .getHoldTransaction()
        .subscribe((transactions: TransactionsHttpResponse) => {
          if (transactions.code === '200') {
            const transactionsTemp = transactions.data;
            if (transactionsTemp && showSavedTransaction) {
              const holdReceipts: HoldReceiptInfo[] = transactionsTemp
                .filter((item) => item.status === 'Hold')
                .map((item) => ({
                  uuid: item.uuid,
                  tempname: item.tempname,
                  amount: item.amount,
                  updatedAt: item.updatedAt,
                }));
              deps.setSavedReceipts(holdReceipts);
            } else {
              deps.setTransactions(
                transactionsTemp.filter((item) => item.status !== 'Hold'),
              );
              this.createFlattenedTransactions(deps);
            }
          }
        });
    } else {
      this.productService
        .getAllTransaction()
        .subscribe((transactions: TransactionsHttpResponse) => {
          if (transactions.code === '200') {
            const transactionsTemp = transactions.data;
            if (transactionsTemp && showSavedTransaction) {
              const holdReceipts: HoldReceiptInfo[] = transactionsTemp
                .filter((item) => item.status === 'Hold')
                .map((item) => ({
                  uuid: item.uuid,
                  tempname: item.tempname,
                  amount: item.amount,
                  updatedAt: item.updatedAt,
                }));
              deps.setSavedReceipts(holdReceipts);
            } else {
              deps.setTransactions(
                transactionsTemp.filter((item) => item.status !== 'Hold'),
              );
              this.createFlattenedTransactions(deps);
            }
          }
        });
    }
  }

  getAllQuotation(deps: TransactionHistoryDeps) {
    this.productService
      .getAllQuotation()
      .subscribe((quotations: QuotationsHttpResponse) => {
        if (quotations.code === '200') {
          deps.setQuotations(quotations.data);
          deps.setFilteredQuotations([...deps.getQuotations()]); // Initialize filtered quotations
          if (deps.getQuotations().length > 0) {
            deps.setSelectedQuotationIndex(0);
            deps.setSelectedQuotationForView(deps.getQuotations()[0].uuid);
          }
        }
      });
  }

  // Search quotations by quotation number, customer name, or mobile number
  onQuotationSearch(deps: TransactionHistoryDeps): void {
    const quotationSearchTerm = deps.getQuotationSearchTerm();
    if (!quotationSearchTerm || quotationSearchTerm.trim() === '') {
      deps.setFilteredQuotations([...deps.getQuotations()]);
      return;
    }

    const searchTerm = quotationSearchTerm.toLowerCase().trim();
    deps.setFilteredQuotations(
      deps.getQuotations().filter((quotation) => {
        const quotationNumber = (
          (quotation as any).quotationnumber ||
          quotation.uuid ||
          ''
        ).toLowerCase();

        const customerName = (quotation.name || '').toLowerCase();

        const mobileNumber = (quotation.user_id || '').toString().toLowerCase();

        return (
          quotationNumber.includes(searchTerm) ||
          customerName.includes(searchTerm) ||
          mobileNumber.includes(searchTerm)
        );
      }),
    );

    const filteredQuotations = deps.getFilteredQuotations();
    if (filteredQuotations.length > 0) {
      const currentSelectionExists = filteredQuotations.some(
        (q) => q.uuid === deps.getSelectedQuotationForView(),
      );
      if (!currentSelectionExists) {
        deps.setSelectedQuotationIndex(0);
        deps.setSelectedQuotationForView(filteredQuotations[0].uuid);
      }
    } else {
      deps.setSelectedQuotationIndex(0);
      deps.setSelectedQuotationForView('');
    }
  }

  // Clear quotation search
  clearQuotationSearch(deps: TransactionHistoryDeps): void {
    deps.setQuotationSearchTerm('');
    deps.setFilteredQuotations([...deps.getQuotations()]);

    if (deps.getQuotations().length > 0) {
      deps.setSelectedQuotationIndex(0);
      deps.setSelectedQuotationForView(deps.getQuotations()[0].uuid);
    }
  }

  // Create flattened transaction items for table display
  createFlattenedTransactions(deps: TransactionHistoryDeps) {
    const flattenedTransactions: any[] = [];
    deps.getTransactions().forEach((transaction) => {
      // Handle both old API structure (customerorderitems) and new API structure (salesitems)
      const items =
        transaction.customerorderitems || transaction.salesitems || [];

      const grandtotalBeforeLoyalty = transaction.grandtotal || 0;
      const loyaltyDeduction = transaction.redeempointamt || 0;
      const actualGrandTotal = grandtotalBeforeLoyalty - loyaltyDeduction;

      flattenedTransactions.push({
        // Transaction level data
        transactionUuid: transaction.uuid,
        user_id: transaction.user_id,
        name:
          transaction.name ||
          (transaction as any).sale?.name ||
          (transaction as any).invoice?.name ||
          '',
        billedBy: transaction.billedBy,
        updatedAt: transaction.updatedAt,
        subtotal: transaction.subtotal,
        total: transaction.total,
        amount: actualGrandTotal,
        taxamount: transaction.taxamount,
        invoiceno: transaction.sale?.invoiceno || transaction.invoiceno,
        invoicedate: transaction.sale?.invoicedate || transaction.invoicedate,
        invoicepdf: transaction.invoicepdf,
        // Item level data
        psid: '',
        hsncode: '',
        qty:
          transaction.salesitems?.reduce(
            (sum, item) => sum + item.quantity,
            0,
          ) || 0,
        price: transaction.subtotal,
        discount: transaction.discountamt,
        redeempointamt: transaction.redeempointamt || 0,
        redeempoints: transaction.redeempoints || 0,
        status: transaction.sale?.status || 'Create',
        createdAt: transaction.createdAt,
      });
    });
    deps.setFlattenedTransactions(flattenedTransactions);

    if (flattenedTransactions.length > 0) {
      deps.setSelectedTransactionIndex(0);
      const firstTransactionUuid = flattenedTransactions[0].transactionUuid;
      deps.setSelectedTransactionForPrint(firstTransactionUuid);
      deps.setCurrentOrderId(firstTransactionUuid);
    }
  }

  highlightSearchTerm(text: string, deps: TransactionHistoryDeps): string {
    const currentSearchTerm = deps.getCurrentSearchTerm();
    if (!currentSearchTerm) return text;

    const regex = new RegExp(`(${currentSearchTerm})`, 'gi');
    return text.replace(regex, '<mark class="search-highlight">$1</mark>');
  }

  // Handle transaction selection for printing
  onTransactionSelect(transactionUuid: string, deps: TransactionHistoryDeps) {
    deps.setSelectedTransactionForPrint(transactionUuid);
    deps.setCurrentOrderId(transactionUuid);
    deps.setSelectedTransactionIndex(
      deps
        .getFlattenedTransactions()
        .findIndex((item) => item.transactionUuid === transactionUuid),
    );
  }

  // Handle quotation selection for viewing
  onQuotationSelect(quotationUuid: string, deps: TransactionHistoryDeps) {
    deps.setSelectedQuotationForView(quotationUuid);
    deps.setSelectedQuotationIndex(
      deps.getQuotations().findIndex((item) => item.uuid === quotationUuid),
    );
  }

  // Handle tab change in history dialog
  onHistoryTabChange(event: any, deps: TransactionHistoryDeps) {
    deps.setActiveHistoryTab(event.index);

    if (deps.getActiveHistoryTab() === 1 && deps.getQuotations().length > 0) {
      setTimeout(() => {
        const searchInput = document.querySelector(
          '.quotation-search-input',
        ) as HTMLInputElement;
        if (searchInput) {
          searchInput.focus();
        }
      }, 100);
    }
  }

  // Refresh data based on active tab
  refreshHistoryData(deps: TransactionHistoryDeps) {
    if (deps.getActiveHistoryTab() === 0) {
      // Refresh transaction history
      this.getAllTransaction(false, deps);
    } else if (deps.getActiveHistoryTab() === 1) {
      // Refresh quotation history
      this.getAllQuotation(deps);
    }
  }

  // View selected quotation - Load quotation details and populate table
  viewQuotation(deps: TransactionHistoryDeps) {
    // Exit exchange mode if active
    if (deps.getIsExchangeOrder()) {
      deps.setIsExchangeOrder(false);
      deps.updateFormDataWithQuotationMode();
      deps.setSelectedInvoiceNo('');
      deps.setRefundCashAmount(0);
    }

    // Exit other modes if active
    if (deps.getIsWholesaleMode()) {
      deps.setIsWholesaleMode(false);
    }
    if (deps.getIsOfferGroupMode()) {
      deps.disableOfferGroupMode();
    }
    if (deps.getIsSuspenseMode()) {
      deps.setIsSuspenseMode(false);
      deps.getCartProducts().forEach((product) => {
        delete product.isgst;
        product.selected = false;
      });
      deps.setProductsTemp(deps.getCartProducts().map((product) => ({ ...product })));
    }
    if (deps.getIsQuotationMode()) {
      deps.setIsQuotationMode(false);
    }

    if (deps.getSelectedQuotationForView()) {
      this.getQuotationDetails(deps.getSelectedQuotationForView(), deps);
    }
  }

  getQuotationDetails(quotationUuid: string, deps: TransactionHistoryDeps) {
    deps.setCartProducts([]);
    this.productService.getQuotationDetails(quotationUuid).subscribe(
      (quotation: TransactionHttpResponse) => {
        if (quotation.code === '200') {
          deps.setHoldOrderId(quotationUuid);

          const quotationItems = quotation.data.customerorderitems || [];

          if (quotation.data.name) {
            deps.setCustomerName(quotation.data.name);
          }

          if (quotation.data.discount > 0) {
            deps.setDiscountMode(
              quotation.data.discountperc > 0 ? 'percentage' : 'amount',
            );
          }

          // Line discounts are saved as entered (% or ₹) plus the per-unit amount. Read the
          // mode back so the recalculation below doesn't reinterpret 10% as ₹10 (same rule
          // the exchange flow uses).
          const discountedItems = quotationItems.filter(
            (item: any) => Number(item.discount) > 0,
          );
          if (discountedItems.length > 0) {
            const isPercentage = discountedItems.some(
              (item: any) =>
                Number(item.discountval ?? item.discountamt) !== Number(item.discount),
            );
            deps.setDiscountMode(isPercentage ? 'percentage' : 'amount');
          }

          deps.setCartProducts(
            deps.mapItemsToProducts(quotationItems, false, true),
          );

          // Lines saved in a secondary unit get that unit's price (discount is handled below)
          deps.restoreSavedLinePrices(false);

          // Only recalculate if user can edit discounts
          if (deps.getIsSalesDiscountEditEnabled()) {
            deps.discountChangeCalc();
          }

          deps.setShowTransactionDialog(false);

          if (deps.getEnableCursorFocus()) {
            setTimeout(() => {
              deps.focusQuantityInput();
            }, 200);
          }
        }
      },
      (error) => {
        this.messageService.add({
          severity: 'error',
          summary: 'Error',
          detail: 'Error loading quotation detail',
          life: 3000,
        });
      },
    );
  }

  // Active Orchestrator Handler for Sales History Dialog
  onTransactionSelectByIndex(
    index: number,
    event: Event | undefined,
    deps: TransactionHistoryDeps,
  ) {
    if (deps.getIsNavigatingProgrammatically()) {
      return;
    }

    if (event) {
      event.stopPropagation();
    }

    deps.setSelectedTransactionIndex(index);
    const flattenedTransactions = deps.getFlattenedTransactions();
    if (index >= 0 && index < flattenedTransactions.length) {
      const transaction = flattenedTransactions[index];
      deps.setSelectedTransactionForPrint(transaction.transactionUuid);
      deps.setCurrentOrderId(transaction.transactionUuid);
    }
  }

  selectQuotationByIndex(index: number, deps: TransactionHistoryDeps) {
    const quotations = deps.getQuotations();
    if (index >= 0 && index < quotations.length) {
      const quotation = quotations[index];
      deps.setSelectedQuotationForView(quotation.uuid);
      deps.setSelectedQuotationIndex(index);

      setTimeout(() => {
        const selectedRow = document.querySelector(
          `tr[data-quotation-index="${index}"]`,
        ) as HTMLElement;
        if (selectedRow) {
          selectedRow.focus();
          selectedRow.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 50);
    }
  }

  // Switch between Transaction History and Quotation History tabs
  switchHistoryTab(goRight: boolean, deps: TransactionHistoryDeps) {
    if (goRight && deps.getActiveHistoryTab() === 0) {
      deps.setActiveHistoryTab(1);
      // Reset quotation selection to first item
      const quotations = deps.getQuotations();
      if (quotations.length > 0) {
        deps.setSelectedQuotationIndex(0);
        deps.setSelectedQuotationForView(quotations[0].uuid);

        // Focus on search input when switching to Quotation History
        setTimeout(() => {
          const searchInput = document.querySelector(
            '.quotation-search-input',
          ) as HTMLInputElement;
          if (searchInput) {
            searchInput.focus();
          }
        }, 100);
      }
    } else if (!goRight && deps.getActiveHistoryTab() === 1) {
      deps.setActiveHistoryTab(0);
      // Reset transaction selection to first item
      if (deps.getFlattenedTransactions().length > 0) {
        deps.setSelectedTransactionIndex(0);
        this.selectTransactionByIndex(0, deps);
      }
    }
  }

  selectTransactionByIndex(index: number, deps: TransactionHistoryDeps) {
    const flattenedTransactions = deps.getFlattenedTransactions();
    if (index >= 0 && index < flattenedTransactions.length) {
      const transaction = flattenedTransactions[index];
      deps.setSelectedTransactionForPrint(transaction.transactionUuid);
      deps.setCurrentOrderId(transaction.transactionUuid);

      // Set flag to prevent radio button event from firing
      deps.setIsNavigatingProgrammatically(true);
      deps.setSelectedTransactionIndex(index);

      // Reset flag after a short delay
      setTimeout(() => {
        deps.setIsNavigatingProgrammatically(false);
      }, 100);

      setTimeout(() => {
        const selectedRow = document.querySelector(
          `tr[data-transaction-index="${index}"]`,
        ) as HTMLElement;
        if (selectedRow) {
          selectedRow.focus();
          selectedRow.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }
      }, 50);
    } else {
      console.log(
        'Invalid index:',
        index,
        'Array length:',
        flattenedTransactions.length,
      );
    }
  }

  getSelectedTransactionOrderId(deps: TransactionHistoryDeps): string {
    if (
      deps.getSelectedTransactionIndex() >= 0 &&
      deps.getTransactions().length > 0
    ) {
      const selectedTransaction =
        deps.getTransactions()[deps.getSelectedTransactionIndex()];
      return selectedTransaction?.orderID || '';
    }
    return '';
  }
}
